import { Box, Button, Input, Link, Typography } from '@material-ui/core'
import React, { useEffect } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import MobileNumberInput from '../../MobileNumberInput/MobileNumberInput'
import { useStyles } from '../Calculator.style'
import { RestartIcon } from '../../SVGIcons'
import { useFormService } from '../../../services/formWithRecaptchaService'
import { checkValidation } from '../../../utils/formValidation'
import { prependCountryCode } from '../../../utils/countryCode'
import { getHtmlTemplateForCalculator } from './CalculatorReportValue.html'

const sectorsOfWork = ['Public', 'Private']
const nationalities = ['Saudi', 'Non Saudi']

const CalculatorReport = (data) => {
  const styles = useStyles()

  const timelineLineRef = React.useRef(null)
  const timelineReportContainer = React.useRef(null)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [name, setName] = React.useState('')
  const [monthlySalary, setMonthlySalary] = React.useState('')
  const [mobileNumber, setMobileNumber] = React.useState('')
  const [nationality, setNationality] = React.useState(nationalities[0])
  const [sectorOfWork, setSectorOfWork] = React.useState(sectorsOfWork[0])

  const { t, location, calculatorType } = data

  const handleSectorChange = (event) => {
    setSectorOfWork(event.target.value)
  }

  useEffect(() => {
    if (
      timelineLineRef &&
      timelineReportContainer &&
      data.type === 'timeline'
    ) {
      const timelineLineHeight = timelineReportContainer.current.getBoundingClientRect()
        .height
      timelineLineRef.current.style.height = `${timelineLineHeight - 90}px`
    }
  }, [])

  const handleNationalityChange = (event) => {
    setNationality(event.target.value)
  }
  const { pathname } = location
  const { handleFormSubmit } = useFormService(pathname, '/api/calculator')

  const handleSubmit = async () => {
    setLoading(true)

    const { validateEntries } = checkValidation(
      name,
      monthlySalary,
      null,
      mobileNumber,
    )

    if (!validateEntries.name || !validateEntries.number) {
      setLoading(false)
      if (!validateEntries.name) {
        setErrorMessage(t('Name is required'))
      } else if (!validateEntries.monthlySalary) {
        setErrorMessage(t('Monthly salary is invalid'))
      } else if (!validateEntries.number) {
        setErrorMessage(t('Mobile number is invalid'))
      } else {
        setErrorMessage(t('Name and Mobile number are required'))
      }
      return false
    }

    const prefixedMobileNumber = prependCountryCode(mobileNumber)

    const calculatorGroup =
      pathname.split('/').length > 2 && pathname.split('/')[2]

    const requestData = {
      source: pathname,
      name,
      'monthly-salary': monthlySalary,
      'phone-number': prefixedMobileNumber,
      'sector-of-work': sectorOfWork,
      nationality,
      'calculator-group': calculatorGroup,
      'calculator-result': getHtmlTemplateForCalculator(data.reportValues),
    }

    try {
      const response = await handleFormSubmit(requestData)
      setLoading(false)
      return response.ok
    } catch (error) {
      setLoading(false)
      return false
    }
  }

  return (
    <Box>
      <Link
        className={styles.resetCalculation}
        onClick={() => data.handleState('form')}
      >
        <span className={styles.resetCalculationText}>
          <Typography>{t('Calculate another lease')}</Typography>
        </span>
        <RestartIcon />
      </Link>

      {data.type === 'timeline' ? (
        <>
          <Box
            ref={timelineReportContainer}
            className={styles.timelineReportContainer}
          >
            <Box ref={timelineLineRef} className={styles.timelineLine}></Box>

            <Box className={styles.timelineDataContainer}>
              {data.reportValues?.subResults && (
                <>
                  {data.reportValues?.subResults.map((flexiData, i) => {
                    return (
                      <Box key={flexiData.label}>
                        {flexiData.eyebrow && (
                          <Typography variant="body1">
                            {t('After')} {flexiData.eyebrow} {t('Year(s)')}
                          </Typography>
                        )}
                        <Typography
                          className={styles.timelineDataLabel}
                          variant="body1"
                        >
                          {t(flexiData.label)}{' '}
                        </Typography>
                        <Typography
                          className={styles.timelineDataValue}
                          variant="h4"
                        >
                          {flexiData.value.toLocaleString()} {flexiData.unit}
                        </Typography>
                      </Box>
                    )
                  })}
                </>
              )}
            </Box>
          </Box>

          <Box className={styles.generalReport}>
            {data.reportValues?.generalResult &&
              data.reportValues.generalResult.map((e) => (
                <Box key={e.title}>
                  <Typography variant="body1">{t(e.title)}</Typography>
                  <Typography variant="h5">
                    {parseInt(e.value, 10).toLocaleString()} {e.unit}
                  </Typography>
                </Box>
              ))}
          </Box>
        </>
      ) : (
        <>
          <Box className={styles.titleResult}>
            {data.reportValues?.mainResult && (
              <>
                <Typography variant="body1">
                  {t(data.reportValues.mainResult.title)}
                </Typography>
                <Typography variant="h4">
                  {parseInt(
                    data.reportValues.mainResult.value,
                    10,
                  ).toLocaleString()}{' '}
                  {data.reportValues.mainResult.unit}
                </Typography>
              </>
            )}
          </Box>
          <Box className={styles.subResult}>
            {data.reportValues?.subResults &&
              data.reportValues.subResults.map((e) => (
                <Box key={e.title}>
                  <Typography variant="body1">{t(e.title)}</Typography>
                  <Typography variant="h4">
                    {parseInt(e.value, 10).toLocaleString()} {e.unit}
                  </Typography>
                </Box>
              ))}
          </Box>
          <Box className={styles.generalReport}>
            {data.reportValues?.generalResult &&
              data.reportValues.generalResult.map((e) => (
                <Box key={e.title}>
                  <Typography variant="body1">{t(e.title)}</Typography>
                  <Typography variant="h5">
                    {e.title === 'APR Rate'
                      ? e.value
                      : parseInt(e.value, 10).toLocaleString()}{' '}
                    {e.unit}
                  </Typography>
                </Box>
              ))}
          </Box>
        </>
      )}

      {errorMessage && (
        <Box className={styles.errorLabelContainer}>
          <Typography variant="body1" className={styles.errorLabel}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      <Box className={styles.reportForm}>
        <Box>
          <Typography variant="body1">{t('Your Name')}</Typography>
          <Input
            fullWidth
            placeholder={t('Enter Full Name')}
            value={name}
            className={styles.reportFormInput}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>
        {calculatorType !== 'self-employed' && (
          <>
            <Box>
              <Typography variant="body1">
                {t('Your Monthly Salary')}
              </Typography>
              <Input
                placeholder={t('Enter Amount in SAR')}
                value={monthlySalary}
                type="number"
                className={styles.reportFormInput}
                onChange={(e) => setMonthlySalary(e.target.value)}
              />
            </Box>
            <Box className={styles.reportSecondForm}>
              <Typography variant="body1">{t('Sector of Work')}</Typography>
              <select value={sectorOfWork} onChange={handleSectorChange}>
                {sectorsOfWork.map((sector) => (
                  <option value={sector} key={sector}>
                    {t(sector)}
                  </option>
                ))}
              </select>
            </Box>
          </>
        )}
        <Box>
          <Typography variant="body1">{t('Your Mobile Number')}</Typography>
          <MobileNumberInput
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
          />
        </Box>
        <Box className={styles.reportSecondForm}>
          <Typography variant="body1">{t('Your Nationality')}</Typography>
          <Box className={styles.radioGroups}>
            {nationalities.map((e, i) => {
              return (
                <Box className={styles.radioItems} key={i}>
                  <input
                    type="radio"
                    name="your_nationality"
                    value={e}
                    {...(i === 0 && { defaultChecked: true })}
                    onChange={handleNationalityChange}
                  />
                  <Typography variant="body1">{t(e)}</Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
      <Box className={styles.calculatorFooter}>
        <Typography variant="body1">
          {t('Calculation figures are subject')}
        </Typography>
        <Button
          id="SUBMIT_BUTTON"
          variant="contained"
          color="secondary"
          className={styles.calculateButton}
          onClick={async () => {
            const response = await handleSubmit()
            if (response) {
              data.handleState('submit')
              if (
                typeof window !== 'undefined' &&
                // eslint-disable-next-line no-undef
                typeof window.gtag === 'function'
              ) {
                // eslint-disable-next-line no-undef
                window.gtag('event', 'submit_button_clicked', { ...data })
              }
            }
          }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress className={styles.circularProgress} size={24} />
          ) : (
            t('Submit')
          )}
        </Button>
      </Box>
    </Box>
  )
}

export default CalculatorReport
